<template>
	<RotationLayout :rotation="rotation" :subtitle="trans('rotations.elections.documentation.meeting_letters.title')">
		<template #actions>
			<a v-if="hasIncomingRepresentatives" :href="route('association.representatives.rotations.meeting-letters.create', rotation.id)">
				<SecondaryButton :color="Color.Purple">
					<i class="fa-solid fa-download me-2"/>
					{{ trans('rotations.elections.documentation.download') }}
				</SecondaryButton>
			</a>
		</template>
		<div v-if="thereAreNoAcceptedLetters && rotation.online && rotation.is_confirmed" class="p-4 flex items-end gap-3 border-b dark:border-gray-800">
			<LabeledObject full :required="rotation.started_at === null">
				<template #label>{{ trans('rotations.started_at') }}</template>
				<p v-if="rotation.started_at">{{ dayjs(rotation.started_at).format(trans('general.dayjs.time')) }}</p>
				<input v-else v-model="rotation.started_at" type="time">
			</LabeledObject>
			<LabeledObject full :required="rotation.ended_at === null">
				<template #label>{{ trans('rotations.ended_at') }}</template>
				<p v-if="rotation.ended_at">{{ dayjs(rotation.ended_at).format(trans('general.dayjs.time')) }}</p>
				<input v-else v-model="rotation.ended_at" type="time">
			</LabeledObject>
			<PrimaryButton v-if="rotation.started_at === null && rotation.ended_at === null" :color="Color.Green" @click.stop="setMeetingTime">
				<i class="fa-solid fa-check"/>
			</PrimaryButton>
		</div>
		<Empty v-if="meetingLetters.length === 0"/>
		<CardGrid v-else>
			<MeetingLetterComponent v-for="meetingLetter in meetingLetters" :key="`rotation-metting-letter-${meetingLetter.id}`" :rotation="rotation" :meeting-letter="meetingLetter"/>
		</CardGrid>
	</RotationLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import RotationLayout from '@/Layouts/RotationLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {computed, type ComputedRef, defineProps, type Ref, ref} from 'vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import {type MeetingLetter} from '@/Types/Periods/Rotations/Documentation/MeetingLetter'
import MeetingLetterComponent from '@/Components/Association/Representatives/MeetingLetter.vue'

const props = defineProps<{
	rotation:Rotation,
	hasIncomingRepresentatives:boolean,
	meetingLetters:Array<MeetingLetter>,
}>()

const thereAreNoAcceptedLetters:ComputedRef<boolean> = computed(() => {
	let acceptedLetterExists = false
	for(let i = 0; !acceptedLetterExists && i < rotation.value.meeting_letters.length; i++){
		acceptedLetterExists = rotation.value.meeting_letters[i].is_accepted
	}
	return !acceptedLetterExists
})

const working:Ref<boolean> = ref(false)
const rotation:Ref<Rotation> = ref(props.rotation)
const meetingLetters:Ref<Array<MeetingLetter>> = ref(props.meetingLetters)

function setMeetingTime():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('association.representatives.rotations.update', rotation.value.id), {
			action: 'time',
			started_at: rotation.value.started_at,
			ended_at: rotation.value.ended_at,
		}).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>